import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiPackage, FiCalendar, FiUserPlus } from 'react-icons/fi';
import { ordersAPI, OrderItem as OrderItemType } from '../api/orders';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { getLocalizedField } from '../utils/localize';
import { getErrorDetail } from '../utils/errorDetail';
import type { SupportedLanguage } from '../i18n';

const Container = styled.div`
  min-height: 80vh;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  background-color: #FAF8F3;
`;

const SuccessCard = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #E8DDD0;
  box-shadow: 0 2px 8px rgba(28, 21, 17, 0.08);
  text-align: center;
  margin-bottom: 2rem;
`;

const SuccessIcon = styled(FiCheckCircle)`
  color: #D4A017;
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  color: #1C1511;
  font-family: Georgia, 'Times New Roman', serif;
  margin-bottom: 1rem;
`;

const OrderNumber = styled.h2`
  color: #6B5E4E;
  font-size: 1.2rem;
  margin-bottom: 2rem;
`;

const OrderDetails = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #E8DDD0;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #1C1511;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #E8DDD0;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  color: #6B5E4E;
`;

const Value = styled.span`
  font-weight: 500;
  color: #1C1511;
`;

const ItemsList = styled.div`
  margin-top: 1rem;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #E8DDD0;

  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #1C1511;
`;

const ItemDetails = styled.div`
  font-size: 0.9rem;
  color: #6B5E4E;
  margin-top: 0.25rem;
`;

const ItemPrice = styled.div`
  font-weight: 500;
  color: #D4A017;
`;

const TotalSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #D4A017;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.2rem;
  font-weight: bold;
  color: #1C1511;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &.primary {
    background-color: #D4A017;
    color: #FFFFFF;

    &:hover {
      background-color: #b8891a;
    }
  }

  &.secondary {
    background-color: #F5F0E8;
    color: #1C1511;
    border: 1px solid #E8DDD0;

    &:hover {
      background-color: #E8DDD0;
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #6B5E4E;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #df1b41;
  font-size: 1.2rem;
`;

const OrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx / OrderDetail.tsx pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<any>({});

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError(t('orderConfirmation.errors.noOrderId'));
        setLoading(false);
        return;
      }

      try {
        const orderData = await ordersAPI.getOrderDetail(parseInt(orderId));
        setOrder(orderData);

        // Fetch product details for each item, matching OrderDetail.tsx's
        // pattern, so the localized name (name_zh) is available to render.
        // Fetches run concurrently (Promise.allSettled) with a per-item
        // timeout so one slow/hung product lookup can't stall the whole
        // post-checkout confirmation screen.
        const productDetails: any = {};
        await Promise.allSettled((orderData.items || []).map(async (item: any) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/products/${item.product_id}`, { signal: controller.signal });
            if (response.ok) {
              productDetails[item.product_id] = await response.json();
            }
          } catch (fetchError) {
            if (fetchError instanceof Error && fetchError.name === 'AbortError') {
              console.warn('Product detail fetch timed out for product', item.product_id);
            } else {
              console.error('Failed to fetch product details:', fetchError);
            }
          } finally {
            clearTimeout(timeoutId);
          }
        }));
        setProducts(productDetails);
      } catch (err: any) {
        setError(getErrorDetail(err) || t('orderConfirmation.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>{t('orderConfirmation.loading')}</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <ButtonGroup>
          <Button className="primary" onClick={() => navigate('/')}>
            {t('orderConfirmation.returnHome')}
          </Button>
        </ButtonGroup>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <ErrorMessage>{t('orderConfirmation.orderNotFound')}</ErrorMessage>
        <ButtonGroup>
          <Button className="primary" onClick={() => navigate('/')}>
            {t('orderConfirmation.returnHome')}
          </Button>
        </ButtonGroup>
      </Container>
    );
  }

  // Resolves the item's display name, preferring the live product fetch
  // (tier 1) and falling back to the server-snapshotted
  // product_name/product_name_zh (backend/app/db/models.py's
  // OrderItem.product_name/product_name_zh properties) when the live
  // /products/{id} lookup misses -- e.g. the product has since been
  // deactivated (active==True filtered, never hard-deleted once it has
  // order history). Mirrors OrderDetail.tsx's getItemDisplayName /
  // AccountOrders.tsx's getQbItemDisplayName.
  const getItemDisplayName = (item: OrderItemType, product: any): string => {
    if (product) {
      return getLocalizedField(product, 'name', language) as string;
    }
    if (language === 'zh' && item.product_name_zh && item.product_name_zh.trim() !== '') {
      return item.product_name_zh;
    }
    if (item.product_name) {
      return item.product_name;
    }
    return t('orderConfirmation.unknownProduct', { id: item.product_id });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <SuccessCard>
        <SuccessIcon />
        <Title>{t('orderConfirmation.title')}</Title>
        <OrderNumber>{t('orderConfirmation.orderNumberLabel')} {order.order_number}</OrderNumber>
        <p>
          {t('orderConfirmation.thankYouMessage', { email: order.customer_email })}
        </p>
      </SuccessCard>

      <OrderDetails>
        <DetailSection>
          <SectionTitle>
            <FiCalendar />
            {t('orderConfirmation.pickupInformation')}
          </SectionTitle>
          <DetailRow>
            <Label>{t('orderConfirmation.pickupDateLabel')}</Label>
            <Value>{formatDate(order.pickup_date)}</Value>
          </DetailRow>
          <DetailRow>
            <Label>{t('orderConfirmation.pickupTimeLabel')}</Label>
            <Value>{formatTime(order.pickup_time)}</Value>
          </DetailRow>
          <DetailRow>
            <Label>{t('orderConfirmation.locationLabel')}</Label>
            <Value>{t('orderConfirmation.pickupLocationValue')}</Value>
          </DetailRow>
        </DetailSection>

        <DetailSection>
          <SectionTitle>
            <FiPackage />
            {t('orderConfirmation.orderItems')}
          </SectionTitle>
          <ItemsList>
            {order.items && order.items.map((item: any) => {
              const product = products[item.product_id];
              return (
                <OrderItem key={item.order_item_id}>
                  <ItemInfo>
                    <ItemName>{getItemDisplayName(item, product)}</ItemName>
                    <ItemDetails>
                      {t('orderConfirmation.quantityLabel')} {item.quantity} × {formatCurrency(item.unit_price)}
                    </ItemDetails>
                  </ItemInfo>
                  <ItemPrice>{formatCurrency(item.subtotal)}</ItemPrice>
                </OrderItem>
              );
            })}
          </ItemsList>

          <TotalSection>
            <TotalRow>
              <span>{t('orderConfirmation.totalAmountLabel')}</span>
              <span>{formatCurrency(order.total_amount)}</span>
            </TotalRow>
          </TotalSection>
        </DetailSection>

        <DetailSection>
          <SectionTitle>{t('orderConfirmation.customerInformation')}</SectionTitle>
          <DetailRow>
            <Label>{t('orderConfirmation.nameLabel')}</Label>
            <Value>{order.customer_name}</Value>
          </DetailRow>
          <DetailRow>
            <Label>{t('orderConfirmation.emailLabel')}</Label>
            <Value>{order.customer_email}</Value>
          </DetailRow>
          <DetailRow>
            <Label>{t('orderConfirmation.phoneLabel')}</Label>
            <Value>{order.customer_phone}</Value>
          </DetailRow>
        </DetailSection>

        {order.special_instructions && (
          <DetailSection>
            <SectionTitle>{t('orderConfirmation.specialInstructions')}</SectionTitle>
            <p>{order.special_instructions}</p>
          </DetailSection>
        )}
      </OrderDetails>

      <ButtonGroup>
        <Button className="primary" onClick={() => navigate('/products')}>
          {t('orderConfirmation.continueShopping')}
        </Button>
        {isAuthenticated ? (
          <Button className="secondary" onClick={() => navigate('/account')}>
            {t('orderConfirmation.viewMyAccount')}
          </Button>
        ) : (
          <Button className="secondary" onClick={() => navigate(`/register?orderId=${orderId}&email=${order?.customer_email}`)}>
            <FiUserPlus style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            {t('orderConfirmation.createAccountAndSaveOrder')}
          </Button>
        )}
      </ButtonGroup>
    </Container>
  );
};

export default OrderConfirmation;